import { HttpClient, type HttpResponse } from '@angular/common/http';
import { Service, inject } from '@angular/core';

import { Observable } from 'rxjs';

import type {
  BookingGeneralState,
  BookingMirState,
  BookingPaymentState,
} from '@core/config/booking-states.config';
import { API_URL } from '@mocks/api.constants';

/**
 * The per-booking workflow view the backend publishes (BookingWorkflowService.view): the three
 * canonical states plus what the lifecycle lets the host do. Skipping registration sets
 * `manualMirManaged` and moves `mirState` to MIR_CANCELLATION_PENDING, then MIR_MANUALLY_MANAGED.
 */
export interface BookingMirWorkflow {
  manualMirManaged: boolean;
  generalState?: BookingGeneralState | null;
  paymentState?: BookingPaymentState | null;
  mirState?: BookingMirState | null;
  /** MIR is active: confirmed, not cancelled, not skipped, not closing. Guest writes need it. */
  canEditGuests?: boolean;
  /** MIR active, the saved guest data complete and no reconciliation pending. */
  canSubmitGuestData?: boolean;
  /** canSubmitGuestData and no pending payment holding the send back. */
  canSendMir?: boolean;
  canCancelLocally?: boolean;
  /** Confirmed, not cancelled, collected by the PMS and still unpaid. */
  canPay?: boolean;
  version?: number;
  guestRevision?: number;
  /** The persisted workflow: the automatic-cancellation flags and the instants their deadlines run from. */
  state?: BookingWorkflowTimeouts | null;
}

/** Automatic cancellations the booking captured on confirmation (`BookingWorkflow` fields, ISO instants). */
export interface BookingWorkflowTimeouts {
  paymentTimeoutEnabled?: boolean | null;
  mirTimeoutEnabled?: boolean | null;
  confirmedAt?: string | null;
  entryLimit?: string | null;
  firstMirRequestedAt?: string | null;
  correctionStartedAt?: string | null;
  /** The guests changed since Hospedajes last got them. */
  changesPending?: boolean | null;
  /** The stay (dates) changed since Hospedajes last got it. */
  mirStayChangesPending?: boolean | null;
  /** The last MIR operation error, kept while a manual closure failed. */
  lastMirError?: string | null;
}

/** errorCode a 409 carries when reactivation is refused (manual-mir reactivation contract). */
export const MIR_REACTIVATION_NOT_ALLOWED = 'MIR_REACTIVATION_NOT_ALLOWED';

@Service()
export class BookingMirWorkflowService {
  private readonly _http = inject(HttpClient);
  private readonly _baseUrl = API_URL.RENTSODY_BO;

  get(id: string): Observable<BookingMirWorkflow> {
    return this._http.get<BookingMirWorkflow>(`${this._baseUrl}/bookings/${id}/workflow`, {
      headers: { 'X-Bypass-Cache': 'true' },
    });
  }

  /** Deactivate MIR: `manualMirManaged: true` keeps the current «Desactivar MIR» behaviour. */
  omit(id: string): Observable<BookingMirWorkflow> {
    return this._http.post<BookingMirWorkflow>(
      `${this._baseUrl}/bookings/${id}/workflow/manual-mir`,
      { manualMirManaged: true, reference: 'Gestión manual' }
    );
  }

  /**
   * Request MIR reactivation (`manualMirManaged: false`). The full response is kept so the caller
   * can tell 200 (already done) from 202 (in progress, poll the booking GET until it clears).
   */
  reactivate(id: string): Observable<HttpResponse<BookingMirWorkflow>> {
    return this._http.post<BookingMirWorkflow>(
      `${this._baseUrl}/bookings/${id}/workflow/manual-mir`,
      { manualMirManaged: false },
      { observe: 'response' }
    );
  }
}
